import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import api from '../../utils/api';
import { Video, RefreshCw, AlertTriangle, CheckCircle, XCircle, Clock } from 'lucide-react';

const LEVEL = {
  ok: { icon: CheckCircle, cls: 'bg-emerald-50 border-emerald-200 text-emerald-800', badge: 'bg-emerald-500' },
  warning: { icon: AlertTriangle, cls: 'bg-amber-50 border-amber-300 text-amber-900', badge: 'bg-amber-500' },
  critical: { icon: XCircle, cls: 'bg-red-50 border-red-300 text-red-900', badge: 'bg-red-600' },
};

const Stat = ({ label, value, sub, testId }) => (
  <div className="p-3 rounded-lg bg-white border" data-testid={testId}>
    <p className="text-xs text-gray-500">{label}</p>
    <p className="text-xl font-bold text-gray-900">{value ?? '—'}</p>
    {sub && <p className="text-[11px] text-gray-500">{sub}</p>}
  </div>
);

export const DIDCreditsCard = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin-portal/did-usage');
      setData(r.data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load D-ID usage');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const level = LEVEL[data?.alert?.level] || LEVEL.ok;
  const Icon = level.icon;
  const pct = data?.total ? Math.min(100, Math.round((data.remaining / data.total) * 100)) : 0;

  return (
    <Card className="border-2 border-slate-200" data-testid="did-credits-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between text-lg">
          <span className="flex items-center gap-2"><Video className="h-5 w-5 text-emerald-600" />ALEC Video (D-ID) Credits</span>
          <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="did-refresh-btn">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && <p className="text-sm text-red-600" data-testid="did-error">{error}</p>}
        {data && !data.configured && <p className="text-sm text-gray-600">D-ID API key is not configured.</p>}
        {data?.configured && (
          <>
            <div className={`flex items-start gap-3 rounded-lg border p-3 ${level.cls}`} data-testid={`did-alert-${data.alert.level}`}>
              <Icon className="h-5 w-5 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-semibold">{data.alert.message}</p>
                <p className="text-xs opacity-80">Warning threshold: {data.alert.threshold} credits (≈ one day of interviews)</p>
              </div>
            </div>

            <div>
              <div className="flex justify-between text-xs text-gray-600 mb-1">
                <span>{data.remaining} of {data.total} credits remaining</span>
                <span>{pct}%</span>
              </div>
              <div className="h-2.5 rounded-full bg-gray-200 overflow-hidden">
                <div className={`h-full ${level.badge} transition-[width] duration-500`} style={{ width: `${pct}%` }} />
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Remaining" value={data.remaining} sub={data.plan_group} testId="did-remaining" />
              <Stat label="Used last 24h" value={data.usage_24h.credits} sub={`${data.usage_24h.interviews_started} interviews started`} testId="did-used-24h" />
              <Stat label="Interviews left" value={data.interviews_left} sub={`~${data.credits_per_interview} credits each`} testId="did-interviews-left" />
              <Stat label="Days left" value={data.days_left ?? '∞'} sub={`need ~${data.daily_need}/day`} testId="did-days-left" />
            </div>

            <div className="flex flex-wrap gap-2 text-xs text-gray-500 items-center">
              <Clock className="h-3 w-3" />
              {data.expire_at && <span>Plan credits expire {new Date(data.expire_at).toLocaleDateString()}</span>}
              <Badge variant="outline">1 credit ≈ {data.seconds_per_credit}s of video</Badge>
              <Badge variant="outline">7d: {data.usage_7d.credits} credits / {data.usage_7d.interviews_started} interviews</Badge>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
